import React, { useEffect, useState, useMemo, useCallback } from "react";
import { secureGet, secureDelete } from "../../config/api";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { PrimaryButton } from "../../components/PrimaryButton";
import PageScroller from "../../components/PageScroller";
import GenericCard from "../Users/GenericCard";
import DeletePopup from "../../components/DeletePopup";
import Filter from "../../components/Filter";
import OptionsMenu from "../../components/OptionsMenu";
import ModuleWrapper from "../../components/ModuleWrapper";
import LoadingLogo from "../../components/LoadingLogo";
import SnackbarNotification from '../../components/SnackbarNotification';

import AddIcon from "../../icons/plusB.svg";
import EditIcon from "../../icons/edit.svg";
import DeleteIcon from "../../icons/trashR.svg";

const Providers = () => {
  const [providers, setProviders] = useState([]);
  const [filteredProviders, setFilteredProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageNumber, setPageNumber] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(30);
  const [filters, setFilters] = useState({ needle: "" });
  const [isDeletePopupVisible, setDeletePopupVisible] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showModule, setShowModule] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const { t } = useTranslation();

  const abortController = useMemo(() => new AbortController(), []);
  const { signal } = abortController;

  const showSnackbar = (message, severity = 'success', manual = false) => {
    setSnackbar({ open: true, message, severity, manual });
  };

  const filterOptions = [
    {
      name: "needle",
      type: "search",
    },
  ];


  const fetchProviders = useCallback(async () => {
    setLoading(true);
    try {
      const response = await secureGet("/providers", showSnackbar, { signal });
      const data = response.data || [];
      setProviders(data);
      setFilteredProviders(data);
      setTotalPages(Math.ceil(data.length / pageSize));
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error("Error fetching providers:", error);
      }
    } finally {
      setLoading(false);
    }
  }, [signal, pageSize]);

  useEffect(() => {
    const abortController = new AbortController();
    fetchProviders();

    return () => {
      abortController.abort();
    };
  }, [abortController, fetchProviders]);

  const applyFilters = useCallback(() => {
    let filtered = [...providers];

    if (filters.needle) {
      const searchTerm = filters.needle.toLowerCase();
      filtered = filtered.filter(
        (provider) =>
          (provider.name || "").toLowerCase().includes(searchTerm) ||
          (provider.contact || "").toLowerCase().includes(searchTerm)
      );
    }

    setFilteredProviders(filtered);
    setTotalPages(Math.ceil(filtered.length / pageSize));
    setPageNumber(0);
  }, [filters, providers, pageSize]);

  useEffect(() => {
    applyFilters();
  }, [filters, applyFilters]);

  const handleAddClick = () => {
    setSelectedProvider(null);
    setShowModule(true);
  };

  const handleEditClick = (provider) => {
    setSelectedProvider(provider);
    setShowModule(true);
  };

  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setDeletePopupVisible(true);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;
    setDeleting(true);
    try {
      const deleteUrl = `/providers/delete/${itemToDelete.id}`;
      await secureDelete(deleteUrl, showSnackbar);
      setProviders((prev) => prev.filter((item) => item.id !== itemToDelete.id));
      setFilteredProviders((prev) =>
        prev.filter((item) => item.id !== itemToDelete.id)
      );
      setTotalPages(Math.ceil(filteredProviders.length / pageSize));
      showSnackbar(t(`confirmation.providerDelete`), 'success');
    } catch (error) {
      console.error("Error deleting provider:", error);
    } finally {
      setDeleting(false);
      setDeletePopupVisible(false);
      setItemToDelete(null);
    }
  };

  const handleModuleClose = (action = "cancel") => {
    if (action === "submit") {

      fetchProviders();
    }
    else if (action === "cancel") {
    }

    setShowModule(false);
    setSelectedProvider(null);
  };

  const getMenuOptions = (provider) => [
    {
      label: t(`actions.modify`),
      icon: EditIcon,
      action: () => handleEditClick(provider),
    },
    {
      label: t(`actions.delete`),
      icon: DeleteIcon,
      color: "#EF476F",
      action: () => handleDeleteClick(provider),
    },
  ];

  const handlePageChange = (page) => {
    if (page >= 0 && page < totalPages) {
      setPageNumber(page);
    }
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setTotalPages(Math.ceil(filteredProviders.length / newPageSize));
    setPageNumber(0);
  };

  const handleFilterChange = (filterName, filterValue) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [filterName]: filterValue,
    }));
  };

  const resetFilters = () => {
    setFilters({ needle: "" });
    setFilteredProviders(providers);
    setPageNumber(0);
    setTotalPages(Math.ceil(providers.length / pageSize));
  };

  const paginatedProviders = filteredProviders.slice(
    pageNumber * pageSize,
    (pageNumber + 1) * pageSize
  );

  return (
    <div>
      <Helmet>
        <title>GrayFox - {t("titles.providers")}</title>
      </Helmet>
      <div className="flex items-center mb-[15px] justify-between">
        <h1 className="text-[40px] font-semibold">{t(`titles.providers`)}</h1>
        <div className="ml-auto text-[16px] flex gap-3">
          <PrimaryButton
            label={t(`primaryButtons.add_provider`)}
            icon={AddIcon}
            onClick={handleAddClick}
          />
        </div>
      </div>

      <Filter
        filters={filterOptions}
        onFilterChange={handleFilterChange}
        resetFilters={resetFilters}
        filterValues={filters}
      />

      <div className="mt-6 grid gap-4 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
        {loading ? (
          <div className="col-span-full flex justify-center">
            <LoadingLogo />
          </div>
        ) : paginatedProviders.length > 0 ? (
          paginatedProviders.map((provider, index) => (
            <GenericCard
              key={index}
              title={provider.name || "Nom non disponible"}
              // subtitle={provider.amount || "Total non disponible"}
              optionsMenu={
                <div onClick={(e) => e.stopPropagation()}>
                  <OptionsMenu options={getMenuOptions(provider)} />
                </div>
              }
              additionalFields={provider.fields || []}
            />
          ))
        ) : (
          <div className="text-center mt-4">
            <p></p>
          </div>
        )}
      </div>

      <PageScroller
        pageNumber={pageNumber}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        pageSize={pageSize}
        onPageSizeChange={handlePageSizeChange}
      />

      {isDeletePopupVisible && (
        <DeletePopup
          open={isDeletePopupVisible}
          onCancel={() => setDeletePopupVisible(false)}
          onConfirm={handleDeleteConfirm}
          loading={deleting}
        />
      )}

      {showModule && (
        <ModuleWrapper
          moduleType="providers"
          mode={selectedProvider ? "modify" : "add"}
          onClose={handleModuleClose}
          onSuccess={(msg) => showSnackbar(msg, 'success')}
          initialValues={selectedProvider || {}}
        />
      )}

      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        manual={snackbar.manual}
      />
    </div>
  );
};

export default Providers;
